import { IReactionDisposer } from 'mobx/lib/internal';
import { reaction } from 'mobx';
import { DisplayObject, Container } from '@tencent/alloy-renderer';
import RenderService from '@src/slide/content-viewer/service/canvas-renderer/entry/base/performance/base-region-render-service';
import { componentMakeCache } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/layered-render/component-cache-help';
import { IComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';

export default class OperationRenderServiceManager {
    static instance: OperationRenderServiceManager;
    static getInstance(): OperationRenderServiceManager {
        if (!this.instance) {
            this.instance = new OperationRenderServiceManager();
        }
        return this.instance;
    }

    public operationLayer: RenderService;
    public overlayLayer: RenderService;
    public currentCanvasElementLayer?: Container; // 当前挂载的canvas画布layers
    public canvasOperationInfo = {
        startIndex: 0, // 操作层元素起始索引
        endIndex: 0, // 操作层元素终点索引
        operationElements: [], // 操作层元素
        overlayElements: [], // 覆盖层元素
    };
    private reaction: IReactionDisposer | null;
    private inCanvasOperation: boolean;

    public initLayer(props, operationCanvasRef, overlayCanvasRef): void {
        if (!this.operationLayer) {
            this.operationLayer = new RenderService({ ...props, canvasElement: operationCanvasRef });
        }
        if (!this.overlayLayer) {
            this.overlayLayer = new RenderService({ ...props, canvasElement: overlayCanvasRef });
        }
        if (!this.reaction) {
            this.init();
        }
    }

    public dispose(): void {
        this.reaction?.();
        this.reaction = null;
    }

    public updateWithData(config): void {
        this.operationLayer?.updateWithData(config);
        this.overlayLayer?.updateWithData(config);
    }

    // 获取所有处于切片层的元素
    public getAllComponents(): DisplayObject[]  {
        const operationChildren = this.operationLayer?.getRenderArea().renderComponent.children || [];
        const overlayChildren = this.overlayLayer?.getRenderArea().renderComponent.children || [];
        return operationChildren.concat(overlayChildren);
    }

    public updatePosition = (renderRectConfig?): void => {
        this.operationLayer?.updatePosition(renderRectConfig);
        this.overlayLayer?.updatePosition(renderRectConfig);
    };

    private init() {
        this.reaction = reaction(() => [
            this.getAppServices().viewableServiceLayer.editingStateManager.isDragging,
            this.getAppServices().viewableServiceLayer.editingStateManager.isResizing,
            this.getAppServices().viewableServiceLayer.editingStateManager.isRotating,
            this.getAppServices().viewableServiceLayer.editingStateManager.isTableRowColResizing,
            this.getAppServices().viewableServiceLayer.selectionService.currentSlide,
        ], ([
            isDragging,
            isResizing,
            isRotating,
            isTableRowColResizing,
        ]) => {
            // 操作态触发分层
            const isOperation = isDragging || isResizing || isRotating || isTableRowColResizing;
            if (isOperation) {
                this.buildSelectedElementInRegionRefresh();
            } else {
                this.revertRegionFresh();
            }
        });
    }

    /**
     * 对被选择的元素进行切片
     */
    private buildSelectedElementInRegionRefresh() {
        const { inCanvasOperation, canvasOperationInfo, currentCanvasElementLayer } = this;
        // 在操作态中，只有首次变更数据时才会进行分片
        if (!inCanvasOperation) {
            const { startIndex, endIndex, overlayElements, operationElements } = canvasOperationInfo;
            if (currentCanvasElementLayer?.children?.length) {
                // 取覆盖层数据，将操作元素的上级元素移动到覆盖层canvas
                let overlayElementNumber = currentCanvasElementLayer.children.length - endIndex - 1;
                while (overlayElementNumber > 0) {
                    const child = currentCanvasElementLayer.children[currentCanvasElementLayer.children.length - 1];
                    currentCanvasElementLayer.removeChild(child);
                    this.overlayLayer.updateComponents(child);
                    // @ts-ignore
                    overlayElements.unshift(child);
                    overlayElementNumber -= 1;
                }
                // 取操作层数据，移动到操作层canvas
                let operationElementNumber = endIndex - startIndex + 1;
                while (operationElementNumber > 0) {
                    const child = currentCanvasElementLayer.children[currentCanvasElementLayer.children.length - 1];
                    currentCanvasElementLayer.removeChild(child);
                    this.operationLayer.updateComponents(child);
                    // @ts-ignore
                    operationElements.unshift(child);
                    componentMakeCache(child as IComponentWrapper);
                    operationElementNumber -= 1;
                }
            }
            this.inCanvasOperation = true;
        }
    }

    /**
     * 还原切片到渲染层
     */
    private revertRegionFresh() {
        const { inCanvasOperation, canvasOperationInfo, currentCanvasElementLayer } = this;
        if (inCanvasOperation) {
            const { operationElements, overlayElements } = canvasOperationInfo;
            if (currentCanvasElementLayer) {
                operationElements.forEach((comp) => {
                    currentCanvasElementLayer.addChild(comp);
                });
                overlayElements.forEach((comp) => {
                    currentCanvasElementLayer.addChild(comp);
                });
            }
            canvasOperationInfo.operationElements = [];
            canvasOperationInfo.overlayElements = [];
            this.inCanvasOperation = false;
        }
    }

    private getAppServices(): IPcEditorAppServices | IMobileEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices | IMobileEditorAppServices;
    }
}
